import React, { useState, useMemo } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, BandPill, ProvenanceFooter, ScoreCell, ProvenanceBadge } from '../components/ui/kits.jsx';
import { ClusteredBars } from '../components/charts/charts.jsx';
import { fmt, fmtCr, bandColor } from '../lib/format.js';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { ChevronRight, ChevronDown } from 'lucide-react';

export default function RegionalZonal() {
  const { data, agg: A } = useData();
  const [mode, setMode] = useState('region');

  // Group projects for each unit
  const projectsByUnit = useMemo(() => {
    if (!A) return {};
    const m = {};
    A.projectsSorted.forEach((p) => {
      const k = mode === 'region' ? p.region : p.zone;
      if (!m[k]) m[k] = [];
      m[k].push(p);
    });
    return m;
  }, [A, mode]);

  if (!data || !A) return null;

  const units = mode === 'region' ? A.regionAgg : A.zoneAgg;
  const top = units[0];
  const bottom = units[units.length - 1];
  const spread = top && bottom ? (top.avgScore - bottom.avgScore) : 0;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-region">
      <div className="card" style={{ padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <SectionTitle>Aggregation Unit</SectionTitle>
          <div className="seg">
            <button className={mode === 'region' ? 'on' : ''} onClick={() => setMode('region')} data-testid="unit-region">By Region</button>
            <button className={mode === 'zone' ? 'on' : ''} onClick={() => setMode('zone')} data-testid="unit-zone">By HDFC Zone</button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Regions: <b>8 canonical</b> · Zones: Z1 {'{'} Central India, South {'}'} · Z2 {'{'} NE, West, Pan India {'}'} · Z3 {'{'} East, N-1, N-2 {'}'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
        {top && (
          <KpiCard hero testid="kpi-region-top"
            label={`Top ${mode === 'region' ? 'Region' : 'Zone'}`}
            value={top.unit}
            sub={`${top.projects} projects · ${fmt(top.avgScore, 2)}${top.members ? ' · ' + top.members.join(', ') : ''}`}
          />
        )}
        <KpiCard testid="kpi-region-spread"
          label="Performance Spread"
          value={fmt(spread, 2)}
          sub={`${top?.unit} (${fmt(top?.avgScore, 2)}) → ${bottom?.unit} (${fmt(bottom?.avgScore, 2)})`}
        />
        <KpiCard testid="kpi-region-count" label="Units Covered" value={String(units.length)} sub={mode === 'region' ? 'Operational regions' : 'HDFC zones'} />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, alignItems: 'center' }}>
            <ProvenanceBadge kind="live" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: '#04294f', borderRadius: 2 }} />NRM</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: '#2e7cbf', borderRadius: 2 }} />H&H</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: '#e23d28', borderRadius: 2 }} />SDLE</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, background: '#9b2d20', borderRadius: 2 }} />PoE</div>
          </div>
        }>
          Tool Performance by {mode === 'region' ? 'Region' : 'Zone'}
        </SectionTitle>
        <ClusteredBars units={units} testid="clustered-bars" />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ProvenanceBadge kind="live" />
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>Click any {mode === 'region' ? 'region' : 'zone'} to expand</span>
          </div>
        }>
          Projects Grouped by {mode === 'region' ? 'Region' : 'Zone'}
        </SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {units.map((u, i) => (
            <UnitBlock
              key={u.unit}
              unit={u}
              projects={(projectsByUnit[u.unit] || []).slice(0, 6)}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      </div>

      <ProvenanceFooter text="Region → Zone mapping is a fixed HDFC-defined tier. Toggle re-runs the aggregation grouping only; project-level filters (topbar) still apply." />
    </div>
  );
}

function UnitBlock({ unit: u, projects, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div data-testid={`unit-block-${u.unit}`} style={{
      border: `1px solid ${open ? 'var(--primary)' : 'var(--line)'}`,
      borderRadius: 10, overflow: 'hidden',
      background: open ? 'var(--surface)' : 'var(--surface-2)',
      transition: 'border-color .15s, background-color .15s',
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`unit-toggle-${u.unit}`}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', border: 0, cursor: 'pointer', textAlign: 'left',
          background: open ? 'var(--primary-soft)' : 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {open ? <ChevronDown size={16} color="var(--primary)" /> : <ChevronRight size={16} color="var(--ink-4)" />}
        <div style={{ fontWeight: 800, fontSize: 14 }}>{u.unit}</div>
        {u.members && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{u.members.join(', ')}</div>}
        <div style={{ fontSize: 11, color: 'var(--ink-4)' }} className="tabular">n={u.projects} · avg {fmt(u.avgScore, 2)}</div>
        <BandPill band={u.band} />
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-4)', fontWeight: 600 }}>
          {open ? `${projects.length} project${projects.length === 1 ? '' : 's'} shown` : 'Show projects'}
        </div>
      </button>
      {open && (
        <div className="fade-up" style={{ padding: 12, borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
            {projects.map((p) => (
              <ProjectCard key={p.code} p={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectCard({ p }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, fontWeight: 800 }}><ProjectCode code={p.code} /></div>
        <ScoreCell score={p.overall} />
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 4 }}>{p.partner || '—'}</div>
      <div style={{ fontSize: 10.5, color: 'var(--ink-4)', display: 'flex', gap: 6, marginTop: 4 }}>
        <span>{p.state || '—'}</span>
        <span>·</span>
        <span style={{ fontWeight: 700, color: p.type === 'HRDP' ? '#042a54' : '#9b2d20' }}>{p.type}</span>
        <span>·</span>
        <span>{fmtCr(p.budgetCr)}</span>
      </div>
    </div>
  );
}
